"use client"

import { useEffect, useState } from "react"
import { isNativeApp, isWebOnlyPath } from "@/lib/native"
import { useObscuredByZone } from "@/hooks/use-obscured-by-zone"
import { useRouter, usePathname } from "next/navigation"
import {
  Home, Map, Globe, Tag, LogIn, UserPlus, LayoutDashboard,
  MessageCircle, ShoppingBag, Briefcase, PlusCircle, Compass,
  ChevronRight, BookOpen, CalendarClock, Armchair, Bookmark, Scissors, Search,
} from "lucide-react"
import Link from "next/link"
import { AuthModal } from "@/components/auth/auth-modal"
import { readClientView } from "@/lib/client-view"
import { useUserRole } from "@/providers/user-role-provider"
import { UserAvatarImg } from "@/components/user-avatar-img"
import { useLandingClientMode } from "@/hooks/use-landing-client-mode"
import { isPublicBookingSurface, isForceDarkBooking, stripLocalePrefix, isGlassNavPath } from "@/i18n/routing"

// Site-wide navigation, every breakpoint. Renders a 64×64 BOOTH launcher
// pinned to the bottom-center; click opens a PlayStation-style 6-tile popout.
//
// Variant resolution:
//   • /dashboard/owner/* → owner tiles
//   • /dashboard/barber/* → barber tiles
//   • everything else (public marketing, listings, /barbers/[slug], etc.)
//     → public tiles, with the auth pair swapped for Dashboard/Account
//     once the user is signed in.
//
// This used to stop at md, where a separate DesktopFloatingNav took over with
// a pill anchored top-left and its own copy of these tiles. That pill is why
// every app page carried a left gutter (md:pl-28 / lg:pl-32) and why dashboard
// pages carried another 42px on top of it — 42px being the x of the pill's
// chair icon. One launcher at the bottom centre serves both, so the gutter is
// gone and the tile list has one definition instead of two.

type Tile = {
  label: string
  icon: typeof Home
  href?: string
  onClick?: () => void
  /** Render the BOOTH wordmark icon instead of a lucide glyph. */
  brand?: boolean
}

type ResolvedVariant = "public" | "owner" | "barber" | "client"

// Routes that render their own focused chrome — the floating nav
// shouldn't hover over a login/signup form or onboarding wizard.
// The stepped create-shop wizard has its own bottom progress bar /
// Next button, so the launcher was floating right on top of it — treat
// it as focused chrome like the auth flow and hide the launcher there.
const HIDE_PREFIXES: ReadonlyArray<string> = ["/auth", "/clients", "/dashboard/owner/create-shop", "/preview"]

// Marketing pages ship their full navigation inside the glass nav's menu
// panel, so this launcher was a second menu over the top of it — two
// overlapping link lists on one screen. That was true of the landing alone
// until the glass nav spread to press / traction / pricing / legal, so the
// list moved to isGlassNavPath and every piece of global chrome reads it.

function isHiddenPath(rawPathname: string | null): boolean {
  if (!rawPathname) return false
  const pathname = stripLocalePrefix(rawPathname)
  // Pages the glass nav owns carry the whole site in their menu panel — this
  // would be a second, competing one on the same screen.
  if (isGlassNavPath(pathname)) return true
  return HIDE_PREFIXES.some(p => pathname === p || pathname.startsWith(`${p}/`))
}

export function FloatingNav() {
  const router = useRouter()
  const pathname = usePathname()
  const [open, setOpen] = useState(false)
  // Persisted Client-mode flag (cookie) so a pro stays in the client lens after
  // leaving /dashboard/client. Read after mount to avoid SSR hydration mismatch.
  const [clientModeActive, setClientModeActive] = useState(false)
  useEffect(() => { setClientModeActive(readClientView()) }, [pathname])
  const [authMode, setAuthMode] = useState<"signup" | "login" | null>(null)
  // In dark mode the launcher + popout switch from solid black to a white
  // translucent glass (0.5px light-gray stroke); light mode keeps the original
  // solid-dark look. On the white surface the content flips to dark for contrast.
  // Read the actual `html.dark` class (set pre-paint by next-themes) rather than
  // resolvedTheme — the latter is undefined on a fresh route load, which left
  // the glass black on SSR-heavy pages like /barbers. The observer keeps it in
  // sync if the theme toggles while mounted.
  // Also treat the force-dark booking storefront as dark: the page is dark for
  // everyone there, so the launcher must take its white-glass (dark) look even
  // when the device/app theme is light — otherwise the solid-black launcher
  // sinks into the dark page.
  const [isDark, setIsDark] = useState(false)
  useEffect(() => {
    const update = () =>
      setIsDark(document.documentElement.classList.contains("dark") || isForceDarkBooking(pathname))
    update()
    const obs = new MutationObserver(update)
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] })
    return () => obs.disconnect()
  }, [pathname])
  // Desktop keeps the black panel the old DesktopFloatingNav modal had: at
  // this size the white glass reads as a sheet of paper dropped on the app,
  // where black reads as chrome. On mobile the glass stays — it sits over a
  // page the thumb is still touching, and the translucency is the point.
  const [isDesktop, setIsDesktop] = useState(false)
  // Set in an effect, not read during render: isNativeApp() is false in SSR
  // and true in the shell, so reading it inline would mismatch on hydration.
  const [native, setNative] = useState(false)
  useEffect(() => setNative(isNativeApp()), [])
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)")
    const update = () => setIsDesktop(mq.matches)
    update()
    mq.addEventListener("change", update)
    return () => mq.removeEventListener("change", update)
  }, [])

  // Everything inside the panel keys off the PANEL's surface, not the page's:
  // a light panel takes dark type, a black panel takes white type. The
  // launcher below still keys off the page, since it sits directly on it.
  const panelLight = isDark && !isDesktop
  const navTxt = panelLight ? "text-zinc-900" : "text-white"
  const navTxtMuted = panelLight ? "text-zinc-500" : "text-white/70"
  const navTxtFaint = panelLight ? "text-zinc-400" : "text-white/50"
  const navHoverBg = panelLight ? "hover:bg-black/5" : "hover:bg-white/5"
  const navAvatarBg = panelLight ? "bg-black/10" : "bg-white/10"

  // Single source of truth for auth + role + profile.
  const { profile, userId, role: currentRole, actualUserType, isClient, clientName, clientAvatarUrl } = useUserRole()
  const isLoggedIn = !!userId
  // Hide while the landing's "Client" toggle is active — that inline surface
  // is meant to be as clean as /clients (no launcher floating over it).
  const landingClientMode = useLandingClientMode()
  // For tile resolution, prefer the toggle preference (currentRole)
  // since that's what the user actively chose. Fall back to actual.
  const role = isLoggedIn ? (currentRole ?? actualUserType) : null

  /**
   * The launcher's own footprint — bottom-6 (24px) plus h-16 (64px), so the
   * bottom 24–88px of the viewport, with slack. A translucent puck parked in
   * the middle of the floor plan's black field reads as a smudge on the artwork
   * rather than a control, and it lands squarely over the chair cards.
   */
  const obscured = useObscuredByZone("bottom", 8, 104)

  // An open menu outranks the fade — the sheet covers the section anyway, and
  // dissolving the control the user just pressed is its own kind of broken.
  const hidden = obscured && !open

  // Close on Esc + lock body scroll while open
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false) }
    window.addEventListener("keydown", onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      window.removeEventListener("keydown", onKey)
      document.body.style.overflow = prev
    }
  }, [open])

  // Auto-close when the route changes (so tapping a tile that maps to
  // an in-place navigation always closes the menu, not just the ones
  // we explicitly call setOpen(false) on).
  useEffect(() => { setOpen(false) }, [pathname])

  // Resolve the active variant.
  //   • On a MODE'S OWN DASHBOARD the URL wins, full stop. A dual-role user
  //     (owner who also cuts) standing on /dashboard/barber was getting OWNER
  //     tiles and an "Owner mode" account row, because their stored role
  //     preference is "owner" and the role check used to outrank the URL —
  //     while the page header (ModeIdentityHeader) reads the URL and said
  //     "Barber Mode". Two chromes on one screen naming different modes.
  //     The same rule already governed /dashboard/client; now all three
  //     dashboards behave alike.
  //   • Off the dashboards (a public page like /pricing), the stored role
  //     still carries: a logged-in owner browsing marketing pages keeps
  //     owner tiles rather than snapping to the public set.
  const variant: ResolvedVariant =
    pathname?.startsWith("/dashboard/owner") ? "owner"
    : pathname?.startsWith("/dashboard/barber") ? "barber"
    : pathname?.startsWith("/dashboard/client") ? "client"
    : isClient ? "client"
    : clientModeActive && isLoggedIn ? "client"
    : isLoggedIn && role === "owner" ? "owner"
    : isLoggedIn && role === "barber" ? "barber"
    : "public"

  // Pro mode for link targets — the variant when it names one (so every link
  // in the panel agrees with the tiles and the mode label), the stored role
  // preference otherwise (public/client surfaces, where "Dashboard" should
  // land on the mode the user last chose).
  const proMode = variant === "owner" || variant === "barber" ? variant : role
  const dashboardHref = proMode === "owner" ? "/dashboard/owner" : "/dashboard/barber"
  const accountHref = proMode === "owner" ? "/dashboard/owner/account" : "/dashboard/barber/account"

  // PRD §11: logged-out visitors on a public BOOKING surface (a barber's
  // profile, /book/*, shop pages) are booking clients, not pro prospects.
  // The pro-marketing tiles (Browse Shops / Job Board / Blog / pro Pricing)
  // don't belong there — just client Pricing + a client-first Sign Up.
  const bookingSurface = !isLoggedIn && isPublicBookingSurface(pathname)

  // Tile sets per variant — mobile public tiles mirror the desktop
  // PublicNavbar modal so the two surfaces feel like one product.
  // Order: Browse Shops · Community · Job Board · Pricing · Blog · CTA.
  const publicTiles: Tile[] = bookingSurface
    ? [
        { label: "Pricing", icon: Tag, href: "/pricing?audience=client" },
        { label: "Sign Up", icon: UserPlus, onClick: () => { setAuthMode("signup"); setOpen(false) } },
      ]
    : [
        { label: "Browse Shops", icon: Map, href: "/listings" },
        { label: "Community", icon: Globe, href: "/community" },
        { label: "Job Board", icon: Briefcase, href: "/hiring" },
        { label: "Pricing", icon: Tag, href: "/pricing" },
        { label: "Blog", icon: BookOpen, href: "/blog" },
        ...(isLoggedIn
          ? [
              { label: "Dashboard", icon: LayoutDashboard, href: dashboardHref } as Tile,
            ]
          : [
              { label: "Sign Up", icon: UserPlus, onClick: () => { setAuthMode("signup"); setOpen(false) } } as Tile,
            ]),
      ]

  const ownerTiles: Tile[] = [
    { label: "Home", icon: Home, href: "/dashboard/owner" },
    { label: "Explore", icon: Compass, href: "/listings" },
    // Pricing sits where Community used to — the subscription tiers are
    // sold to exactly the people standing here, and the owner's community
    // surface is already reachable through Hiring. Keeps the grid 3×2.
    { label: "Pricing", icon: Tag, href: "/pricing" },
    { label: "Hiring", icon: Briefcase, href: "/hiring" },
    { label: "Messages", icon: MessageCircle, href: "/dashboard/owner/messages" },
    { label: "Add Shop", icon: PlusCircle, href: "/dashboard/owner/create-shop" },
  ]

  const barberTiles: Tile[] = [
    { label: "Home", icon: Home, href: "/dashboard/barber" },
    // Booths — the barber's saved shops, upcoming sessions, and booth
    // rental plans (moved off the home overview into a dedicated page).
    { label: "Booths", icon: Armchair, href: "/dashboard/barber/booths", brand: true },
    { label: "Browse", icon: Map, href: "/listings" },
    // Bookings is back in its original slot. It briefly gave way to Pricing on
    // the reasoning that Schedule's calendar covered it — but Schedule shows
    // BOOTH sessions (which chair, which shop), while Bookings is the barber's
    // own client appointments. Different surfaces, and a barber taking client
    // money needs the second one reachable in one tap.
    { label: "Bookings", icon: CalendarClock, href: "/dashboard/barber/bookings" },
    { label: "Messages", icon: MessageCircle, href: "/dashboard/barber/messages" },
    // Search replaced Community (2026-09-19): on desktop this menu was the
    // only nav, and it had no way to reach /search — the city/rent search
    // the whole barber funnel starts from. Browse (/listings) is the grid
    // view; Search is the query view. Network stays reachable from the
    // public navbar and the barber profile.
    { label: "Search", icon: Search, href: "/search" },
    // Label says 'Job Board' in barber mode (they're browsing for
    // openings) and 'Hiring' in owner mode (they're posting them).
    { label: "Job Board", icon: Briefcase, href: "/hiring" },
    // Rewards — the points/equipment shop. Matches the desktop sidebar's
    // ShoppingBag + "Rewards" so the barber sees the same entry everywhere.
    { label: "Rewards", icon: ShoppingBag, href: "/dashboard/barber/rewards" },
    // Pricing stays reachable in every mode (the booth-rent platform fee is
    // pitched to barbers standing right here), but it goes LAST: with ten
    // tiles the 3-column grid leaves one on its own row, and that should be
    // the least load-bearing entry, not a hole in the middle.
    { label: "Pricing", icon: Tag, href: "/pricing" },
  ]

  // Client tiles — the consumer surface. No barber/owner/hiring links; just the
  // client's own home, shop/barber discovery, and their saved list.
  const clientTiles: Tile[] = [
    { label: "Home", icon: Home, href: "/dashboard/client" },
    { label: "Explore", icon: Map, href: "/listings" },
    { label: "Discover", icon: Scissors, href: "/barbers" },
    // Client-audience pricing — same deep link the public booking surfaces
    // use, so clients land on the client tab, not the pro tiers.
    { label: "Pricing", icon: Tag, href: "/pricing?audience=client" },
  ]

  // Marketing surfaces come out of every tile set at once. Filtering here
  // rather than in each array means a tile added to one variant later can't
  // quietly reintroduce a marketing page in the app. The list itself lives in
  // lib/native.ts so the three navs can't drift apart.
  const tilesForVariant =
    variant === "owner" ? ownerTiles
    : variant === "barber" ? barberTiles
    : variant === "client" ? clientTiles
    : publicTiles
  const tiles = native ? tilesForVariant.filter((t) => !isWebOnlyPath(t.href)) : tilesForVariant

  const handleTileClick = (tile: Tile) => {
    if (tile.onClick) {
      tile.onClick()
    } else if (tile.href) {
      // For same-route taps, just close the menu — router.push triggers
      // a re-render but the URL doesn't change so our pathname effect
      // wouldn't auto-close.
      if (tile.href === pathname) {
        setOpen(false)
      } else {
        router.push(tile.href)
        // Pathname effect closes it once navigation completes; close
        // immediately too so the open state doesn't flash on slow nav.
        setOpen(false)
      }
    }
  }

  // Suppress on focused-chrome routes (auth flow) and while the landing's
  // Client toggle is active. Kept after hooks so the rules-of-hooks order is
  // stable across renders.
  if (isHiddenPath(pathname) || landingClientMode) return null

  return (
    <>
      {/* Floating launcher — every breakpoint. Sits at bottom-6 (24px) so
          it reads as a standard FAB position with safe-area clearance,
          and stops eating up the bottom action rows on shorter pages. */}
      <button
        type="button"
        // Toggle, not open-only. Pressing the launcher while the menu was
        // already open did nothing, so a quick double-tap felt broken —
        // the second press had no effect and no feedback.
        onClick={() => setOpen(v => !v)}
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
        // Springy press: a deeper squash on press and a back-out curve on
        // release, so it overshoots slightly and reads as a pump. Short
        // duration so rapid taps each register instead of smearing into one
        // slow tween.
        // aria-hidden + tabIndex while faded: an invisible button that still
        // takes focus and still answers a screen reader is worse than no
        // button. pointer-events go with it so it can't swallow a click on the
        // section underneath.
        aria-hidden={hidden}
        tabIndex={hidden ? -1 : 0}
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-16 h-16 rounded-full flex items-center justify-center backdrop-blur-xl hover:scale-105 active:scale-[0.88] motion-reduce:hover:scale-100 motion-reduce:active:scale-100"
        style={{
          // --launcher-lift: a page can raise the launcher off a bottom row
          // it would otherwise cover (the chat composer sets it on the phone).
          bottom: "calc(1.5rem + var(--launcher-lift, 0px))",
          opacity: hidden ? 0 : 1,
          pointerEvents: hidden ? "none" : undefined,
          // Slow out, quick back: leaving is the part that should feel like the
          // button dissolving into the section rather than blinking off. The
          // press spring keeps its own short duration.
          transition: `opacity ${hidden ? 420 : 260}ms ease-out, transform 150ms cubic-bezier(0.34,1.56,0.64,1)`,
          ...(isDark
            ? {
                background: "linear-gradient(180deg, rgba(255,255,255,0.60), rgba(255,255,255,0.42))",
                backdropFilter: "blur(20px) saturate(200%) brightness(2.8)",
                WebkitBackdropFilter: "blur(20px) saturate(200%) brightness(2.8)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5), 0 0 0 0.5px rgba(0,0,0,0.08), 0 2px 14px rgba(0,0,0,0.30)",
              }
            : { backgroundColor: "#100E0E", boxShadow: "0 0 0 1px rgba(255,255,255,0.10), 0 2px 8px rgba(0,0,0,0.18)" }),
        }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/booth-chair-logo.png"
          alt="BOOTH"
          className="w-8 h-8 object-contain"
          style={isDark ? { filter: "brightness(0)", opacity: 0.55 } : { opacity: 0.9 }}
        />
      </button>

      {/* Popout — bottom-anchored just above the launcher, at every
          breakpoint. The wrapping div is full-viewport so taps anywhere
          outside the popout close it, but it has no background tint — the
          page stays visible behind, matching how the launcher looks when
          closed (just sitting on top of the page). */}
      {open && (
        <div
          className="fixed inset-0 z-40 animate-in fade-in duration-200"
          onClick={(e) => { if (e.currentTarget === e.target) setOpen(false) }}
        >
          {/* Popout card. Radius matches the My Booths card (rounded-
              [2.5rem]) so the app's rounding reads as one system. Sits at
              bottom-24 (96px) — clear of the launcher, which spans 24–88px
              (bottom-6 + h-16) — leaving an 8px breather above the button
              instead of tucking behind it. */}
          <div
            className="absolute left-3 right-3 bottom-24 max-w-xs md:max-w-md mx-auto rounded-[2.5rem] backdrop-blur-xl overflow-hidden"
            style={{
              bottom: "calc(6rem + var(--launcher-lift, 0px))",
              ...(
              panelLight
                ? {
                    background: "linear-gradient(180deg, rgba(255,255,255,0.92), rgba(255,255,255,0.84))",
                    backdropFilter: "blur(28px) saturate(200%) brightness(2.8)",
                    WebkitBackdropFilter: "blur(28px) saturate(200%) brightness(2.8)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5), 0 0 0 0.5px rgba(0,0,0,0.08), 0 10px 34px rgba(0,0,0,0.28)",
                  }
                : {
                    background: "rgba(0,0,0,0.9)",
                    boxShadow: "inset 0 0 0 0.5px rgba(255,255,255,0.12), 0 10px 34px rgba(0,0,0,0.45)",
                  }),
            }}
          >
{/* Tiles — naked icon + label, 3-col grid. Tightened to match
                the public modal density: gap-y-3, with top padding (pt-6)
                trimmed to sit closer to the account row's bottom padding. */}
            <div className={`px-5 pt-6 ${isLoggedIn && profile ? "pb-3" : "pb-8"}`}>
              <div className={`grid gap-y-3 gap-x-2 ${tiles.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
                {tiles.map((tile, i) => {
                  const Icon = tile.icon
                  const active = !!tile.href && tile.href === pathname
                  // Outer columns hug the modal edges (left col → start,
                  // right col → end) so the tiles sit at the same margin as
                  // the avatar (left) and chevron (right) in the account row
                  // — killing the dead black space at the sides. Each tile
                  // is a fixed width (w-20) with centered content, so within
                  // a column every icon lands on the same vertical axis
                  // regardless of label length (Community vs Add Shop).
                  // The 2-tile booking-surface set centers both instead —
                  // edge-hugging two lone tiles reads as a layout bug.
                  const col = i % 3
                  const justifySelf =
                    tiles.length === 2 ? "justify-self-center"
                    : col === 0 ? "justify-self-start"
                    : col === 2 ? "justify-self-end"
                    : "justify-self-center"
                  return (
                    <button
                      key={tile.label}
                      type="button"
                      onClick={() => handleTileClick(tile)}
                      className={`group ${justifySelf} w-20 md:w-24 flex flex-col items-center justify-center gap-2 hover:scale-[1.06] focus-visible:outline-none transition-transform ${
                        active ? navTxt : `${navTxtMuted} ${panelLight ? "hover:text-zinc-900" : "hover:text-white"}`
                      }`}
                      style={{
                        animation: `psTileIn 360ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 60}ms backwards`,
                      }}
                    >
                      {tile.brand ? (
                        /* BOOTH icon — wide ∞ wordmark; lock to icon height (h-6)
                           and let width scale so it matches the other glyphs.
                           Black on light surfaces, white on the dark modal. */
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={panelLight ? "/images/booth-icon-black.png" : "/images/booth-icon-white.png"}
                          alt=""
                          className="h-4 w-auto object-contain"
                        />
                      ) : (
                        <Icon className={`w-6 h-6 ${panelLight ? "text-black" : ""}`} strokeWidth={1.5} />
                      )}
                      {/* whitespace-nowrap: "Job Board" and "Community" were wrapping to two
                          lines inside the old w-16 tile, which knocked the whole row
                          out of alignment. The tile is wide enough for the longest
                          label now, and a label can never wrap again. */}
                      <span className={`text-[11px] font-medium tracking-wide whitespace-nowrap ${panelLight ? "text-zinc-900" : ""}`}>{tile.label}</span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Account row — only when authenticated. The whole row is a
                Link to the user's account page; the trailing chevron
                makes the affordance obvious. Mode label below the name
                tells the user which mode they're in at a glance.
                Padding matches the tiles row above (px-5) so the
                avatar visually aligns with the leftmost tile column. */}
            {isLoggedIn && (profile || isClient) && (() => {
              // Keyed on the resolved VARIANT, not the stored role preference,
              // so the row can never name a different mode than the tiles
              // above it (the dual-role "Owner mode" row over barber tiles).
              const acctHref = variant === "client" ? "/dashboard/client/account" : accountHref
              const modeLabel = variant === "client" ? "Client" : proMode === "owner" ? "Owner" : "Barber"
              // A pro (barber/owner) keeps their real role even while viewing Client
              // mode — enables the mode toggle below.
              const isProUser = actualUserType === "owner" || actualUserType === "barber"
              const displayName = variant === "client"
                ? (clientName || profile?.full_name || profile?.first_name || "My account")
                : (profile?.full_name || profile?.first_name || "Account")
              const acctAvatar = variant === "client" ? clientAvatarUrl : profile?.avatar_url
              return (
              <div className="px-5 pb-3">
                <div className={`flex items-center py-1.5 rounded-2xl ${navHoverBg} transition-colors`}>
                  <Link
                    href={acctHref}
                    onClick={() => setOpen(false)}
                    className="flex items-center flex-1 min-w-0 hover:opacity-80 transition-opacity"
                  >
                    {/* Avatar rides in a w-20 zone that mirrors the LEFT tile
                        column above, so it lands on the exact same vertical
                        axis as the leftmost link icons. */}
                    <div className="w-20 md:w-24 flex justify-center flex-shrink-0">
                      <div className={`relative w-8 h-8 rounded-full overflow-hidden ${navAvatarBg}`}>
                        {/* UserAvatarImg falls back to the initial when the URL
                            is missing OR dead (old storage file deleted by a
                            re-upload) — no broken-image glyph in the pill. */}
                        <UserAvatarImg
                          src={acctAvatar}
                          name={displayName}
                          fallbackClassName={`w-full h-full flex items-center justify-center ${navTxtMuted} text-xs font-semibold`}
                        />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-[12px] font-semibold ${navTxt} truncate leading-tight`}>
                        {displayName}
                      </p>
                      <p className={`text-[10px] font-medium leading-tight ${panelLight ? "text-zinc-800" : "text-white/80"}`}>
                        {modeLabel} mode
                      </p>
                    </div>
                  </Link>
                  {/* Chevron rides in a matching w-20 zone → same vertical axis
                      as the RIGHT tile column's icons. */}
                  <div className="w-20 md:w-24 flex justify-center flex-shrink-0">
                    <Link
                      href={acctHref}
                      onClick={() => setOpen(false)}
                      aria-label="Account"
                      className={`flex items-center justify-center w-7 h-7 rounded-full ${navTxtFaint} ${panelLight ? "hover:text-zinc-900 hover:bg-black/5" : "hover:text-white hover:bg-white/10"} transition-colors`}
                    >
                      <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.75} />
                    </Link>
                  </div>
                </div>
              </div>
              )
            })()}
          </div>

          <style jsx>{`
            @keyframes psTileIn {
              from { opacity: 0; transform: translateY(12px) scale(0.98); }
              to   { opacity: 1; transform: translateY(0) scale(1); }
            }
          `}</style>
        </div>
      )}

      <AuthModal
        open={authMode !== null}
        onOpenChange={(o) => { if (!o) setAuthMode(null) }}
        defaultMode={authMode || "signup"}
        defaultRole={bookingSurface ? "client" : "barber"}
      />
    </>
  )
}
